import { FC, useLayoutEffect, useRef } from 'react';
import { ChatInput } from '../components/ChatInput';
import { ChatMessage, StreamingMessage } from '../components/ChatMessage';
import aiAvatar from '@/assets/images/ai-avatar.png';
import { AIChatMessage } from '@/types/api/aiChat';

/** この位置まで遡ったら古い履歴を追加で読む（px） */
const LOAD_OLDER_THRESHOLD = 48;

interface AIChatProps {
  messages: AIChatMessage[];
  isLoading: boolean;
  isSending: boolean;
  isStreaming: boolean;
  streamingContent: string;
  error: string | null;
  hasMoreHistory: boolean;
  isLoadingOlder: boolean;
  onSendMessage: (message: string) => void;
  onLoadOlder: () => void;
  onClose: () => void;
}

const styles = {
  // ポップアップの中を埋める。全画面固定にはしない
  overlay: {
    flex: 1,
    minHeight: 0,
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
  },
  headerTitle: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    flex: 1,
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold' as const,
    color: '#333',
  },
  subtitle: {
    fontSize: '12px',
    color: '#888',
    marginTop: '2px',
  },
  closeButton: {
    width: '32px',
    height: '32px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#666',
  },
  placeholder: {
    width: '32px',
  },
  messagesContainer: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as const,
    padding: '16px',
    backgroundColor: '#FAFAFA',
    // Chromeは上に要素が挿入されると scrollTop を自動補正する(scroll anchoring)。
    // 古い履歴の継ぎ足しでは自分で位置を補正しているため、二重に効いてずれる。
    // iOS Safari はこの機能を持たないので自動補正には任せられず、切って自前で揃える
    overflowAnchor: 'none' as const,
  },
  initialMessage: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  // ChatMessage と同じ ai-avatar.png を同じ寸法で使う
  aiIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    marginRight: '8px',
    flexShrink: 0,
    objectFit: 'cover' as const,
  },
  initialBubble: {
    maxWidth: '80%',
    padding: '12px 16px',
    borderRadius: '18px 18px 18px 4px',
    backgroundColor: '#F0F0F5',
    color: '#333333',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  loadingDots: {
    display: 'flex',
    gap: '4px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#1E55E6',
    animation: 'bounce 1.4s infinite ease-in-out both',
  },
  errorMessage: {
    padding: '12px 16px',
    margin: '8px 16px',
    backgroundColor: '#FFEBEE',
    color: '#C62828',
    borderRadius: '8px',
    fontSize: '14px',
    textAlign: 'center' as const,
  },
  inputContainer: {
    backgroundColor: '#FFFFFF',
    paddingBottom: 'env(safe-area-inset-bottom)',
  },
  // 遡って古い履歴を読み込んでいる間の表示
  loadingOlder: {
    padding: '8px 0 16px',
    textAlign: 'center' as const,
    fontSize: '12px',
    color: '#888',
  },
};

/**
 * 会話が1件も無いときだけ出す案内
 *
 * 通常はホーム画面の声がけが1件目のAI発言として履歴に入るため、これは出ない。
 * 声がけを経由せずチャットを開いた場合に画面が空になるのを防ぐためのもの。
 * Phase2で保証以外（車検・修理・サブスクなど）も扱うようになったので、
 * 保証専用だった文面は使わない。
 */
const EMPTY_STATE_MESSAGE =
  'こんにちは。お車のことは何でもお聞きください。車検・保証・修理・お支払いなど、気になることにお答えします。';

export const AIChat: FC<AIChatProps> = ({
  messages,
  isLoading,
  isSending,
  isStreaming,
  streamingContent,
  error,
  hasMoreHistory,
  isLoadingOlder,
  onSendMessage,
  onLoadOlder,
  onClose,
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastIdRef = useRef<number | null>(null);
  const lengthRef = useRef(0);
  const scrollHeightBeforeRef = useRef(0);

  // スクロール位置の制御。
  // 「上に継ぎ足された」時は見ていた位置を保ち、それ以外（新着）は最下部へ。
  // 判定は末尾メッセージで行う。末尾が変わらないまま件数が増えたなら
  // 上に足されたということ（先頭IDの比較だと、同じ範囲が再取得された場合に
  // 継ぎ足しと判定できず最下部へ飛んでしまう）。
  // scrollIntoView は祖先要素も動かしてポップアップ背後の画面までスクロール
  // させてしまうため使わず、コンテナの scrollTop を直接操作する。
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;

    if (!container) {
      return;
    }

    const lastId = messages[messages.length - 1]?.id ?? null;
    const isPrepended =
      messages.length > lengthRef.current &&
      lastId !== null &&
      lastId === lastIdRef.current;

    if (isPrepended) {
      container.scrollTop +=
        container.scrollHeight - scrollHeightBeforeRef.current;
    } else {
      container.scrollTop = container.scrollHeight;
    }

    lastIdRef.current = lastId;
    lengthRef.current = messages.length;
    scrollHeightBeforeRef.current = container.scrollHeight;
  }, [messages, streamingContent]);

  // 上端付近まで遡ったら古い履歴を読む
  const handleScroll = () => {
    const container = messagesContainerRef.current;

    if (!container || !hasMoreHistory || isLoadingOlder) {
      return;
    }

    if (container.scrollTop <= LOAD_OLDER_THRESHOLD) {
      onLoadOlder();
    }
  };

  return (
    <div style={styles.overlay}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <div style={styles.placeholder} />
        <div style={styles.headerTitle}>
          <span style={styles.title}>マイくるAI</span>
          <span style={styles.subtitle}>いつでもお話しましょう</span>
        </div>
        <button
          style={styles.closeButton}
          onClick={onClose}
          aria-label="閉じる"
        >
          &times;
        </button>
      </div>

      {/* メッセージ一覧 */}
      <div
        ref={messagesContainerRef}
        style={styles.messagesContainer}
        onScroll={handleScroll}
      >
        {/* 追加読み込み中の表示 */}
        {isLoadingOlder && <div style={styles.loadingOlder}>読み込み中...</div>}

        {/* 会話が空の時だけ出す。声がけが履歴に入っていればそれが1件目になる */}
        {!isLoading && !hasMoreHistory && messages.length === 0 && (
          <div style={styles.initialMessage}>
            <img src={aiAvatar} alt="" style={styles.aiIcon} />
            <div style={styles.initialBubble}>{EMPTY_STATE_MESSAGE}</div>
          </div>
        )}

        {/* ローディング */}
        {isLoading && (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingDots}>
              <div style={{ ...styles.dot, animationDelay: '-0.32s' }} />
              <div style={{ ...styles.dot, animationDelay: '-0.16s' }} />
              <div style={styles.dot} />
            </div>
            <style>{`
              @keyframes bounce {
                0%, 80%, 100% { transform: scale(0); }
                40% { transform: scale(1); }
              }
            `}</style>
          </div>
        )}

        {/* 対話履歴 */}
        {messages.map((message, index) => {
          // 選択肢のタップ送信は最新のAI発言だけに出す。
          // 過去の発言の選択肢まで押せると、どの質問への回答か分からなくなる。
          const isLatestAiMessage =
            index === messages.length - 1 && message.role !== 'user';

          return (
            <ChatMessage
              key={message.id}
              message={message}
              onSelectChoice={isLatestAiMessage ? onSendMessage : undefined}
              disabled={isSending || isStreaming}
            />
          );
        })}

        {/* ストリーミング中のメッセージ */}
        {isStreaming && streamingContent && (
          <StreamingMessage content={streamingContent} />
        )}

        {/* 送信中のインジケータ */}
        {isSending && !isStreaming && (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingDots}>
              <div style={{ ...styles.dot, animationDelay: '-0.32s' }} />
              <div style={{ ...styles.dot, animationDelay: '-0.16s' }} />
              <div style={styles.dot} />
            </div>
          </div>
        )}

        {/* エラーメッセージ */}
        {error && <div style={styles.errorMessage}>{error}</div>}
      </div>

      {/* 入力エリア */}
      <div style={styles.inputContainer}>
        <ChatInput
          onSend={onSendMessage}
          disabled={isSending || isStreaming}
          placeholder="保証について質問してください..."
        />
      </div>
    </div>
  );
};
